/**
 * Helpers de presentación del dashboard de Learning (doc de Andrea, ajustes
 * 14/09 §2). Sin componentes (react-refresh): formateos, clases y CSV.
 */
import { currentLocale } from '@/lib/locale';
import type { TFunction } from 'i18next';
import { isoDay, plazoFor, type LearningCategory, type LearningDashboardRow, type LearningStatus, type Plazo } from '@/../../shared/people-learning';

export const ALL = '__all__';

/** Colores del doc: Sin iniciar rojo · En progreso naranja · Completado verde. */
export const STATUS_BADGE: Record<LearningStatus, string> = {
  sin_iniciar: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  en_progreso: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  completado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  no_elegible: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

/** Estado / categoría traducidos (los labels del shared son solo ES). */
export const statusLabel = (t: TFunction, s: LearningStatus) => t(`people_learning.dash.status.${s}`, s);
export const categoryLabel = (t: TFunction, c: LearningCategory) => t(`people_learning.dash.category.${c}`, c);

export const fmtMs = (ms: number) => {
  if (!ms) return '—';
  const m = Math.round(ms / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

/** Fecha corta ('22 sep 2026') de un ISO, un 'd/m/aaaa' del community map o un timestamp. */
export const fmtDate = (raw: string | null | undefined) => {
  const day = isoDay(raw);
  return day ? new Date(`${day}T12:00:00`).toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};

export const rowPlazo = (r: LearningDashboardRow, today: string): Plazo => plazoFor(r, today);

/** Texto del plazo para la tabla ("2 días hábiles", "Vencido hace 3", "Completado a tiempo"). */
export function plazoLabel(t: TFunction, p: Plazo): { text: string; className: string } {
  const k = 'people_learning.dash.plazo';
  switch (p.kind) {
    case 'due':
      return {
        text: p.days === 0 ? t(`${k}.today`, 'Vence hoy') : t(`${k}.days_left`, { count: p.days, defaultValue: '{{count}} días hábiles' }),
        className: p.days <= 2 ? 'text-red-700 dark:text-red-300 font-semibold' : 'text-muted-foreground',
      };
    case 'overdue':
      return { text: t(`${k}.overdue`, { count: p.days, defaultValue: 'Vencido hace {{count}} días hábiles' }), className: 'text-red-700 dark:text-red-300 font-semibold' };
    case 'on_time':
      return { text: t(`${k}.on_time`, 'Completado a tiempo'), className: 'text-emerald-700 dark:text-emerald-300' };
    case 'late':
      return { text: t(`${k}.late`, { count: p.days, defaultValue: 'Completado {{count}} días hábiles tarde' }), className: 'text-orange-700 dark:text-orange-300' };
    default:
      return { text: '—', className: 'text-muted-foreground' };
  }
}

/** "3 años" / "8 meses" / "20 días" desde la fecha de ingreso. */
export function antiguedad(t: TFunction, startDate: string | null, today: string): string {
  const start = isoDay(startDate);
  if (!start) return '—';
  const a = new Date(`${start}T00:00:00Z`).getTime();
  const b = new Date(`${today}T00:00:00Z`).getTime();
  if (Number.isNaN(a) || b < a) return '—';
  const days = Math.floor((b - a) / 86400000);
  const k = 'people_learning.dash.antiguedad';
  if (days >= 365) return t(`${k}.years`, { count: Math.floor(days / 365), defaultValue: '{{count}} años' });
  if (days >= 30) return t(`${k}.months`, { count: Math.floor(days / 30), defaultValue: '{{count}} meses' });
  return t(`${k}.days`, { count: days, defaultValue: '{{count}} días' });
}

export interface RowFilters {
  search: string;
  area: string;
  leader: string;
  country: string;
  status: string;
  /** Solo filas con videos marcados sin tiempo de visualización. */
  suspiciousOnly: boolean;
}

export const EMPTY_FILTERS: RowFilters = { search: '', area: ALL, leader: ALL, country: ALL, status: ALL, suspiciousOnly: false };

export function applyFilters(rows: LearningDashboardRow[], f: RowFilters): LearningDashboardRow[] {
  let out = rows;
  if (f.area !== ALL) out = out.filter((r) => r.area === f.area);
  if (f.leader !== ALL) out = out.filter((r) => (r.leaderEmail ?? r.leaderName) === f.leader);
  if (f.country !== ALL) out = out.filter((r) => r.country === f.country);
  if (f.status !== ALL) out = out.filter((r) => r.status === f.status);
  if (f.suspiciousOnly) out = out.filter((r) => r.engagement.level === 'suspicious');
  const q = f.search.trim().toLowerCase();
  if (q) out = out.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q));
  return [...out].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export function filterOptions(rows: LearningDashboardRow[]) {
  const leaders = new Map<string, string>();
  for (const r of rows) {
    const key = r.leaderEmail ?? r.leaderName;
    if (key) leaders.set(key, r.leaderName || key);
  }
  return {
    areas: [...new Set(rows.map((r) => r.area).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    countries: [...new Set(rows.map((r) => r.country).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    leaders: [...leaders.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label, 'es')),
  };
}

/**
 * CSV: el guard anti-fórmula nació acá y ahora vive en `@/lib/csv` para toda
 * la plataforma (auditoría 18/09). Learning conserva su separador `;`.
 */
export { csvCell } from '@/lib/csv';
import { downloadCsv as downloadCsvShared } from '@/lib/csv';
export function downloadCsv(filename: string, header: string[], lines: unknown[][]): void {
  downloadCsvShared(filename, header, lines, { delimiter: ';' });
}

/** Color de cumplimiento para tiles/gauges (doc: ≥80 verde, ≥50 amarillo, resto naranja). */
export function pctTone(pct: number): 'good' | 'mid' | 'low' {
  return pct >= 80 ? 'good' : pct >= 50 ? 'mid' : 'low';
}
export const TONE_TILE: Record<'good' | 'mid' | 'low', string> = {
  good: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200',
  mid: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-200',
  low: 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/30 dark:border-orange-800 dark:text-orange-200',
};
export const TONE_HEX: Record<'good' | 'mid' | 'low', string> = { good: '#1CC9B9', mid: '#F5B301', low: '#FE805B' };
/** Tono de KpiCard según el % (cards de cumplimiento). */
export const TONE_KPI: Record<'good' | 'mid' | 'low', 'green' | 'amber' | 'orange'> = { good: 'green', mid: 'amber', low: 'orange' };
